import styled from 'styled-components';
import { Skeleton } from 'ui/skeleton';
import { MeterOptions } from './types';

type LegendProps = {
  options: MeterOptions[];
  defaultColors: string[];
  isLoading: boolean;
  isEnabled?: boolean;
};

const CircleIcon = styled.div<{ color: Required<MeterOptions['color']> }>`
  border-radius: 50%;
  height: ${({ theme }) => theme.spacing.values.threeQuarter};
  width: ${({ theme }) => theme.spacing.values.threeQuarter};
  background-color: ${({ color }) => color};
`;

const CircleSkeleton = styled(Skeleton)`
  border-radius: 50%;
  height: ${({ theme }) => theme.spacing.values.threeQuarter};
  width: ${({ theme }) => theme.spacing.utils.pxToRem(20)};
  background-color: #f3f4f5;
`;

const LabelSkeleton = styled(Skeleton)`
  background-color: #f3f4f5;
  min-width: ${({ theme }) => theme.spacing.values.double};
`;

const LegendContainer = styled.div`
  display: flex;
  column-gap: ${({ theme }) => theme.spacing.values.plusHalf};
`;

const Legend = styled.div`
  ${({ theme }) => theme.styles.body.body2};
  align-items: center;
  color: ${({ theme }) => theme.colors.textColors.textSecondary};
  column-gap: ${({ theme }) => theme.spacing.values.half};
  display: flex;
  flex-direction: row;
`;

export const DATA_TEST = {
  legend: 'meter-legend',
  label: 'meter-legend-label',
  skeletonCircle: 'meter-legend-skeleton-circle',
  skeletonLabel: 'meter-legend-skeleton-label',
};

const LegendSkeletonLoader = () => (
  <LegendContainer data-test={DATA_TEST.legend}>
    <Legend>
      <CircleSkeleton data-test={DATA_TEST.skeletonCircle} />
      <LabelSkeleton data-test={DATA_TEST.skeletonLabel} />
    </Legend>
    <Legend>
      <CircleSkeleton data-test={DATA_TEST.skeletonCircle} />
      <LabelSkeleton data-test={DATA_TEST.skeletonLabel} />
    </Legend>
  </LegendContainer>
);

export const MeterLegend = ({
  options,
  defaultColors,
  isLoading,
  isEnabled = false,
}: LegendProps) => {
  if (!isEnabled) return null;
  if (isLoading) return <LegendSkeletonLoader />;

  return (
    <LegendContainer data-test={DATA_TEST.legend}>
      {options.map(({ name, legendContent, color }, i) => {
        const label = legendContent ?? name;
        return (
          <Legend key={`meter-legend-${label}`}>
            <CircleIcon color={color ?? defaultColors[i]} />
            <div data-test={`${DATA_TEST.label}-${label}`}>{label}</div>
          </Legend>
        );
      })}
    </LegendContainer>
  );
};
